export type ImagePosition = {
    x: number;
    y: number;
};

type ValueWrapper<T> = {
    value: T;
};

type PositionLike = {
    x?: unknown;
    y?: unknown;
    left?: unknown;
    top?: unknown;
    horizontal?: unknown;
    vertical?: unknown;
};

function clampPercent(value: number, min = 0, max = 100): number {
    return Math.min(max, Math.max(min, value));
}

function parseNumber(input: unknown): number | null {
    if (input == null || input === '') return null;
    const n = parseFloat(String(input));
    return Number.isNaN(n) ? null : n;
}

export function parseImagePosition(
    value: unknown,
    fallback: ImagePosition = { x: 50, y: 50 }
): ImagePosition {
    const raw =
        value && typeof value === 'object' && 'value' in value
            ? (value as ValueWrapper<unknown>).value
            : value;

    if (raw == null || raw === '') {
        return { x: fallback.x, y: fallback.y };
    }

    if (typeof raw === 'object' && !Array.isArray(raw)) {
        const positionRaw = raw as PositionLike;

        const xNum = parseNumber(
            positionRaw.x ?? positionRaw.left ?? positionRaw.horizontal
        );
        const yNum = parseNumber(
            positionRaw.y ?? positionRaw.top ?? positionRaw.vertical
        );

        const hasX = Number.isFinite(xNum);
        const hasY = Number.isFinite(yNum);

        if (hasX || hasY) {
            return {
                x: clampPercent(hasX ? (xNum as number) : fallback.x),
                y: clampPercent(hasY ? (yNum as number) : fallback.y),
            };
        }
    }

    const str = String(raw).trim();
    if (!str) return { x: fallback.x, y: fallback.y };

    const numbers = str.match(/-?\d+(\.\d+)?/g);
    if (numbers?.length) {
        const xNum = parseNumber(numbers[0]);
        const yNum = parseNumber(numbers[1]);

        return {
            x: clampPercent(xNum ?? fallback.x),
            y: clampPercent(yNum ?? fallback.y),
        };
    }

    const lower = str.toLowerCase();
    let x: number | undefined;
    let y: number | undefined;

    if (lower.includes('left')) x = 0;
    if (lower.includes('right')) x = 100;
    if (lower.includes('top')) y = 0;
    if (lower.includes('bottom')) y = 100;

    if (lower.includes('center')) {
        if (x == null) x = 50;
        if (y == null) y = 50;
    }

    if (x != null || y != null) {
        return {
            x: clampPercent(x ?? fallback.x),
            y: clampPercent(y ?? fallback.y),
        };
    }

    return { x: fallback.x, y: fallback.y };
}
